import {
  extractBlobAssetKey,
  normalizeMediaFetchUrl,
  resolveMediaFetchUrl,
} from '@/helpers/preview/blob-asset-key.helper';

interface BlobCacheEntry {
  objectUrl: string;
  sourceUrl: string;
  deckScope?: string;
  lastAccess: number;
}

const blobCache = new Map<string, BlobCacheEntry>();
/** Monotonic per assetKey — stale downloads must not overwrite cache or inFlight. */
const flightGenerationByKey = new Map<string, number>();

type InFlightEntry = {
  sourceUrl: string;
  generation: number;
  promise: Promise<string>;
};

const inFlight = new Map<string, InFlightEntry>();
/** Latest superseded blob per key — kept alive until the next refresh revokes it. */
const supersededObjectUrlByKey = new Map<string, string>();

function markSupersededObjectUrl(assetKey: string, objectUrl: string): void {
  const older = supersededObjectUrlByKey.get(assetKey);
  if (older && older !== objectUrl) {
    URL.revokeObjectURL(older);
  }
  supersededObjectUrlByKey.set(assetKey, objectUrl);
}

function nextFlightGeneration(assetKey: string): number {
  const generation = (flightGenerationByKey.get(assetKey) ?? 0) + 1;
  flightGenerationByKey.set(assetKey, generation);
  return generation;
}

function isActiveFlight(assetKey: string, generation: number): boolean {
  return flightGenerationByKey.get(assetKey) === generation;
}

/** Total slide blobs retained across decks (480px previews — typically a few MB). */
const MAX_CACHE_ENTRIES = 300;
const DEFAULT_PREFETCH_CONCURRENCY = 6;

function deckScopeFromCacheKey(key: string): string | undefined {
  const marker = ':slide-';
  const idx = key.lastIndexOf(marker);
  if (idx >= 0) {
    return key.slice(0, idx);
  }
  return undefined;
}

function touchCacheEntry(key: string, entry: BlobCacheEntry): void {
  entry.lastAccess = Date.now();
  blobCache.set(key, entry);
}

function evictCacheEntriesForScope(scope: string): void {
  for (const [key, entry] of blobCache.entries()) {
    if (entry.deckScope === scope) {
      URL.revokeObjectURL(entry.objectUrl);
      blobCache.delete(key);
    }
  }
}

function evictOldestDeckScope(exceptScope?: string): void {
  const scopes = new Map<string, number>();
  for (const entry of blobCache.values()) {
    if (!entry.deckScope || entry.deckScope === exceptScope) {
      continue;
    }
    const prev = scopes.get(entry.deckScope) ?? Number.POSITIVE_INFINITY;
    scopes.set(entry.deckScope, Math.min(prev, entry.lastAccess));
  }
  if (scopes.size === 0) {
    const oldestKey = blobCache.keys().next().value;
    if (oldestKey !== undefined) {
      const entry = blobCache.get(oldestKey);
      if (entry) {
        URL.revokeObjectURL(entry.objectUrl);
        blobCache.delete(oldestKey);
      }
    }
    return;
  }
  let oldestScope: string | undefined;
  let oldestAccess = Number.POSITIVE_INFINITY;
  for (const [scope, access] of scopes) {
    if (access < oldestAccess) {
      oldestAccess = access;
      oldestScope = scope;
    }
  }
  if (oldestScope) {
    evictCacheEntriesForScope(oldestScope);
  }
}

function ensureCacheCapacity(deckScope?: string): void {
  while (blobCache.size >= MAX_CACHE_ENTRIES) {
    evictOldestDeckScope(deckScope);
    if (blobCache.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = blobCache.keys().next().value;
      if (oldestKey === undefined) {
        break;
      }
      const entry = blobCache.get(oldestKey);
      if (entry) {
        URL.revokeObjectURL(entry.objectUrl);
        blobCache.delete(oldestKey);
      }
    }
  }
}

function resolveAssetKey(url: string, assetKeyOverride?: string): string {
  return assetKeyOverride ?? extractBlobAssetKey(url);
}

/**
 * Synchronous lookup — used to avoid a loading flash when a slide was prefetched.
 * When `expectedSourceUrl` is set, returns a hit only if the cached blob matches that signed URL.
 */
export function getCachedPreviewBlobObjectUrl(assetKey: string, expectedSourceUrl?: string): string | undefined {
  const entry = blobCache.get(assetKey);
  if (!entry) {
    return undefined;
  }
  if (expectedSourceUrl !== undefined && entry.sourceUrl !== expectedSourceUrl) {
    return undefined;
  }
  touchCacheEntry(assetKey, entry);
  return entry.objectUrl;
}

export type PreviewBlobPrefetchEntry = {
  url: string;
  cacheKey: string;
};

/**
 * Warm the blob cache for an entire slide deck (best-effort, bounded concurrency).
 * Typical 480px slide previews are small enough to retain in memory for the active deck.
 */
export async function prefetchPreviewBlobEntries(
  entries: readonly PreviewBlobPrefetchEntry[],
  options?: { concurrency?: number; deckScope?: string },
): Promise<void> {
  if (entries.length === 0) {
    return;
  }

  const concurrency = Math.max(1, options?.concurrency ?? DEFAULT_PREFETCH_CONCURRENCY);
  let cursor = 0;

  const worker = async () => {
    while (cursor < entries.length) {
      const index = cursor;
      cursor += 1;
      const entry = entries[index];
      if (!entry?.url || !entry.cacheKey) {
        continue;
      }
      try {
        await ensurePreviewBlobCached(entry.url, entry.cacheKey, options?.deckScope);
      } catch {
        // Prefetch is best-effort; on-demand load still works.
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, entries.length) }, () => worker()));
}

/** Fetch a signed media URL once and reuse an object URL (avoids CDN CORS for pdf.js). */
export async function ensurePreviewBlobCached(
  url: string,
  assetKeyOverride?: string,
  deckScope?: string,
): Promise<string> {
  if (!url) {
    return url;
  }

  const normalizedUrl = normalizeMediaFetchUrl(url);
  const fetchUrl = resolveMediaFetchUrl(url);
  const assetKey = resolveAssetKey(normalizedUrl, assetKeyOverride);
  const resolvedDeckScope = deckScope ?? deckScopeFromCacheKey(assetKey);

  const cached = blobCache.get(assetKey);
  if (cached) {
    if (cached.sourceUrl === normalizedUrl) {
      touchCacheEntry(assetKey, cached);
      return cached.objectUrl;
    }
    markSupersededObjectUrl(assetKey, cached.objectUrl);
    blobCache.delete(assetKey);
  }

  const existingFlight = inFlight.get(assetKey);
  if (existingFlight?.sourceUrl === normalizedUrl) {
    return existingFlight.promise;
  }

  const generation = nextFlightGeneration(assetKey);
  const requestedSourceUrl = normalizedUrl;

  const download = (async (): Promise<string> => {
    try {
      const response = await fetch(fetchUrl, { credentials: 'omit' });
      if (!response.ok) {
        throw new Error(`Failed to fetch blob asset (${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      if (!isActiveFlight(assetKey, generation)) {
        URL.revokeObjectURL(objectUrl);
        const latest = inFlight.get(assetKey);
        if (latest) {
          return latest.promise;
        }
        const cachedAfterRace = blobCache.get(assetKey);
        if (cachedAfterRace) {
          return cachedAfterRace.objectUrl;
        }
        throw new Error('Preview blob download superseded');
      }

      ensureCacheCapacity(resolvedDeckScope);
      blobCache.set(assetKey, {
        objectUrl,
        sourceUrl: requestedSourceUrl,
        deckScope: resolvedDeckScope,
        lastAccess: Date.now(),
      });
      if (inFlight.get(assetKey)?.generation === generation) {
        inFlight.delete(assetKey);
      }
      return objectUrl;
    } catch (error) {
      if (!isActiveFlight(assetKey, generation)) {
        const latest = inFlight.get(assetKey);
        if (latest) {
          return latest.promise;
        }
        const cachedAfterRace = blobCache.get(assetKey);
        if (cachedAfterRace) {
          return cachedAfterRace.objectUrl;
        }
      }
      if (inFlight.get(assetKey)?.generation === generation) {
        inFlight.delete(assetKey);
      }
      throw error;
    }
  })();

  inFlight.set(assetKey, { sourceUrl: requestedSourceUrl, generation, promise: download });
  return download;
}

/** @internal Test helper */
export function __clearPreviewBlobCacheForTests(): void {
  for (const entry of blobCache.values()) {
    URL.revokeObjectURL(entry.objectUrl);
  }
  blobCache.clear();
  inFlight.clear();
  flightGenerationByKey.clear();
  for (const objectUrl of supersededObjectUrlByKey.values()) {
    URL.revokeObjectURL(objectUrl);
  }
  supersededObjectUrlByKey.clear();
}
